import { Button, Spinner, Tabs } from '@/ui-kit'
import { OverlayScrollBars } from '@/ui-kit/components/OverlayScrollbars'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { type ReactNode, useState } from 'react'
import { useFetchEnrollmentCounts, useFetchEnrollments } from '../api'
import { BUCKET_STATUSES } from '../utils/enrollmentBucket'
import { type EnrollmentFilter } from './EnrollmentFilterChips'
import { EnrollmentRow } from './EnrollmentRow'

const PAGE_LIMIT = 20
export const COLUMN_COUNT = 4

export const HeaderCell = ({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={clsx(
      'text-gray-90 border-b border-solid border-gray-100 px-4 py-3 font-semibold whitespace-nowrap',
      align === 'right' ? 'text-right' : 'text-left'
    )}
  >
    {children}
  </th>
)

export const Cell = ({
  children,
  align = 'left',
  className
}: {
  children: ReactNode
  align?: 'left' | 'right'
  className?: string
}) => (
  <td
    className={clsx(
      'border-b border-solid border-gray-100 px-4 py-4 align-middle whitespace-nowrap',
      align === 'right' ? 'text-right' : 'text-left',
      className
    )}
  >
    {children}
  </td>
)

export const EnrollmentsList = () => {
  const { t } = useLingui()
  const [filter, setFilter] = useState<EnrollmentFilter>('all')
  const statuses = filter === 'all' ? undefined : BUCKET_STATUSES[filter]
  const query = useFetchEnrollments({ limit: PAGE_LIMIT, statuses })
  const countsQuery = useFetchEnrollmentCounts()

  const entries = query.data?.pages.flatMap((p) => p.data) ?? []
  const hasMore = !!query.hasNextPage
  const isFirstLoad = query.isPending
  const isFetchingMore = query.isFetchingNextPage

  const totalCount = countsQuery.isLoading ? '…' : (countsQuery.data?.total ?? '—')
  const activeCount = countsQuery.isLoading ? '…' : (countsQuery.data?.active ?? '—')
  const finishedCount = countsQuery.isLoading
    ? '…'
    : (countsQuery.data?.completed ?? 0) + (countsQuery.data?.closed ?? 0)

  const tabs: { value: EnrollmentFilter; title: string }[] = [
    { value: 'all', title: t`All (${totalCount})` },
    { value: 'active', title: t`Active (${activeCount})` },
    { value: 'finished', title: t`Finished (${finishedCount})` }
  ]

  const filtersTabs = (
    <div className="max-mobile:flex-col flex justify-between gap-2">
      <div className="text-subtitle font-semibold">
        <Trans>Missions</Trans>
      </div>
      <Tabs
        tabs={tabs}
        value={filter}
        onChange={(tab) => setFilter(tab.value)}
        size="small"
        className="max-mobile:max-w-full h-10 max-w-max"
      />
    </div>
  )

  if (isFirstLoad) {
    return (
      <div className="flex flex-col gap-4">
        {filtersTabs}
        <div className="flex w-full justify-center py-10">
          <Spinner />
        </div>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {filtersTabs}
        <div className="m-auto max-w-[350px] rounded-xl bg-gray-100/30 p-6 text-center">
          <div className="text-gray-90">
            <Trans>No enrollments yet</Trans>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {filtersTabs}
      <div className="">
        <OverlayScrollBars>
          <table className="w-full table-auto border-separate [border-spacing:0_12px]">
            <thead className="max-tablet:hidden">
              <tr>
                <HeaderCell>
                  <Trans>Mission</Trans>
                </HeaderCell>
                <HeaderCell>
                  <Trans>Status</Trans>
                </HeaderCell>
                <HeaderCell align="right">
                  <Trans>Earnings</Trans>
                </HeaderCell>
                <HeaderCell> </HeaderCell>
              </tr>
            </thead>
            <tbody>
              {entries.map((enrollment) => (
                <EnrollmentRow key={enrollment.id} enrollment={enrollment} />
              ))}
            </tbody>
          </table>
        </OverlayScrollBars>
      </div>
      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="bordered-gray" size="small" onClick={() => query.fetchNextPage()} isLoading={isFetchingMore}>
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}
    </div>
  )
}
